import React from 'react';
import {
    Styler,
    Container,
    FlexGrid2,
    Header as HeaderCore,
    Stacker,
    ActionButton,
    useBreakpoints,
    MenuIcon,
    Sidebar,
    ModalContent,
    ModalHeader,
    useEventCallback,
} from '@honeycomb/ui-core';
import { BedIcon, CarIcon, PlaneIcon, SuitcaseIcon } from 'components/icons';
import { useGlobalContext } from '@honeycomb/data';
import { NetflightsLogo } from '../images/NetflightsLogo';
import { CallUsButton } from './OpeningHours';
import { HeaderNFooter, replaceResourceString } from './LayoutUtil';

export type MastheadProps = {
    /**
     * If `true`, the menu links will be hidden. This is for on the checkout page.
     * @default false
     */
    hideMenu?: boolean;
};

export function Masthead(props: MastheadProps) {
    const {
        resourceStrings: { HC_HEADER_AND_FOOTER_CONTENT, SEARCH_TABS_HOLIDAY_LABEL, SEARCH_TABS_HOTEL_LABEL },
    } = useGlobalContext();

    const content = React.useMemo<HeaderNFooter | null>(
        () => (HC_HEADER_AND_FOOTER_CONTENT ? (JSON.parse(HC_HEADER_AND_FOOTER_CONTENT) as HeaderNFooter) : null),
        [HC_HEADER_AND_FOOTER_CONTENT]
    );

    const { hideMenu = false } = props;

    const breakpoints = useBreakpoints();

    const [menuOpen, setMenuOpen] = React.useState<boolean>(false);

    const openMenu = useEventCallback(() => {
        setMenuOpen(true);
    });

    const closeMenu = useEventCallback(() => {
        setMenuOpen(false);
    });

    if (!content || !content.header) return null;

    const {
        header: { flightIcon, flightPlusHotelIcon, hotelIcon, carHireIcon, phoneIcon, myTripIcon },
    } = content;

    return (
        <>
            <HeaderCore component="nav" disableGutters={true} backgroundColor="primary">
                <Container>
                    {!breakpoints.largeUp ? (
                        <>
                            <Styler pos="absolute" top="50%" left="50%" transform="translateX(-50%) translateY(-50%)">
                                {/* eslint-disable-next-line @next/next/no-html-link-for-pages */}
                                <a href="/" title="Netflights">
                                    <NetflightsLogo />
                                </a>
                            </Styler>
                            <Stacker
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                flexWrap="nowrap"
                                spacing={4}
                            >
                                <div>
                                    {!hideMenu && (
                                        <ActionButton
                                            icon={<MenuIcon />}
                                            color="text.inverse"
                                            onClick={openMenu}
                                            round={true}
                                            alignEdge="start"
                                        />
                                    )}
                                </div>
                                <Stacker direction="row" spacing={2}>
                                    {phoneIcon.show && <CallUsButton phoneIcon={phoneIcon} />}
                                    {myTripIcon.show && (
                                        <ActionButton
                                            icon={<SuitcaseIcon />}
                                            href={myTripIcon.link}
                                            color="text.inverse"
                                            round={true}
                                            alignEdge="end"
                                        />
                                    )}
                                </Stacker>
                            </Stacker>
                        </>
                    ) : (
                        <FlexGrid2 container={true} alignItems="center" flexWrap="nowrap" spacing={4}>
                            <FlexGrid2 xs="hold">
                                {/* eslint-disable-next-line @next/next/no-html-link-for-pages */}
                                <a href="/" title="Netflights">
                                    <NetflightsLogo />
                                </a>
                            </FlexGrid2>
                            <FlexGrid2 xs="fill">
                                {!hideMenu && (
                                    <Styler c="text.inverse">
                                        <Stacker component="ul" spacing={2} direction="row" role="menu">
                                            {flightIcon.show && (
                                                <li role="none">
                                                    <ActionButton
                                                        href={flightIcon?.link}
                                                        icon={<PlaneIcon />}
                                                        color="text.inverse"
                                                        direction="row"
                                                        role="menuitem"
                                                    >
                                                        {flightIcon.text}
                                                    </ActionButton>
                                                </li>
                                            )}
                                            {flightPlusHotelIcon.show && (
                                                <li role="none">
                                                    <ActionButton
                                                        href={flightPlusHotelIcon?.link}
                                                        icon={[
                                                            <PlaneIcon key="HeaderPlaneicon" />,
                                                            <BedIcon key="HeaderBedicon" />,
                                                        ]}
                                                        color="text.inverse"
                                                        direction="row"
                                                        role="menuitem"
                                                    >
                                                        {replaceResourceString(
                                                            flightPlusHotelIcon.text,
                                                            'SEARCH_TABS_HOLIDAY_LABEL',
                                                            SEARCH_TABS_HOLIDAY_LABEL
                                                        )}
                                                    </ActionButton>
                                                </li>
                                            )}
                                            {hotelIcon && (
                                                <li role="none">
                                                    <ActionButton
                                                        href={hotelIcon?.link}
                                                        icon={<BedIcon />}
                                                        color="text.inverse"
                                                        direction="row"
                                                        role="menuitem"
                                                    >
                                                        {replaceResourceString(
                                                            hotelIcon.text,
                                                            'SEARCH_TABS_HOTEL_LABEL',
                                                            SEARCH_TABS_HOTEL_LABEL
                                                        )}
                                                    </ActionButton>
                                                </li>
                                            )}
                                            {carHireIcon.show && (
                                                <li role="none">
                                                    <ActionButton
                                                        href={carHireIcon?.link}
                                                        icon={<CarIcon />}
                                                        color="text.inverse"
                                                        direction="row"
                                                        role="menuitem"
                                                    >
                                                        {carHireIcon.text}
                                                    </ActionButton>
                                                </li>
                                            )}
                                        </Stacker>
                                    </Styler>
                                )}
                            </FlexGrid2>
                            <FlexGrid2 xs="hold">
                                <Styler c="text.inverse">
                                    <Stacker component="ul" alignItems="center" spacing={2} direction="row">
                                        {phoneIcon.show && (
                                            <li role="none">
                                                <CallUsButton phoneIcon={phoneIcon} />
                                            </li>
                                        )}
                                        {myTripIcon.show && (
                                            <li role="none">
                                                <ActionButton
                                                    href={myTripIcon?.link}
                                                    icon={<SuitcaseIcon />}
                                                    color="text.inverse"
                                                    direction="row"
                                                >
                                                    {myTripIcon.text}
                                                </ActionButton>
                                            </li>
                                        )}
                                    </Stacker>
                                </Styler>
                            </FlexGrid2>
                        </FlexGrid2>
                    )}
                </Container>
            </HeaderCore>

            {!breakpoints.largeUp && !hideMenu && (
                <Sidebar side="start" open={menuOpen} onClose={closeMenu} width="s">
                    <ModalHeader
                        closeButton={true}
                        bg="primary.base"
                        c="inverse"
                        CloseButtonProps={{ color: 'white' }}
                        borderStyle="none"
                    >
                        NetFlights
                    </ModalHeader>
                    <ModalContent disableGutters={true}>
                        <Styler bg="tertiary" px={4} c="text.inverse" h="100%">
                            <Stacker component="ul" spacing={2} role="menu">
                                {flightIcon.show && (
                                    <li role="none">
                                        <ActionButton
                                            href={flightIcon?.link}
                                            icon={<PlaneIcon />}
                                            color="text.inverse"
                                            direction="row"
                                            fullWidth={true}
                                            role="menuitem"
                                        >
                                            {flightIcon.text}
                                        </ActionButton>
                                    </li>
                                )}
                                {flightPlusHotelIcon.show && (
                                    <li role="none">
                                        <ActionButton
                                            href={flightPlusHotelIcon?.link}
                                            icon={[
                                                <PlaneIcon key="HeaderPlaneicon" />,
                                                <BedIcon key="HeaderBedicon" />,
                                            ]}
                                            color="text.inverse"
                                            direction="row"
                                            fullWidth={true}
                                            role="menuitem"
                                        >
                                            {replaceResourceString(
                                                flightPlusHotelIcon.text,
                                                'SEARCH_TABS_HOLIDAY_LABEL',
                                                SEARCH_TABS_HOLIDAY_LABEL
                                            )}
                                        </ActionButton>
                                    </li>
                                )}
                                {hotelIcon.show && (
                                    <li role="none">
                                        <ActionButton
                                            href={hotelIcon?.link}
                                            icon={<BedIcon />}
                                            color="text.inverse"
                                            direction="row"
                                            fullWidth={true}
                                            role="menuitem"
                                        >
                                            {replaceResourceString(
                                                hotelIcon.text,
                                                'SEARCH_TABS_HOTEL_LABEL',
                                                SEARCH_TABS_HOTEL_LABEL
                                            )}
                                        </ActionButton>
                                    </li>
                                )}

                                {carHireIcon.show && (
                                    <li role="none">
                                        <ActionButton
                                            href={carHireIcon?.link}
                                            icon={<CarIcon />}
                                            color="text.inverse"
                                            direction="row"
                                            fullWidth={true}
                                            role="menuitem"
                                        >
                                            {carHireIcon.text}
                                        </ActionButton>
                                    </li>
                                )}
                                {myTripIcon.show && (
                                    <li role="none">
                                        <ActionButton
                                            href={myTripIcon?.link}
                                            icon={<SuitcaseIcon />}
                                            color="text.inverse"
                                            direction="row"
                                            fullWidth={true}
                                            role="menuitem"
                                        >
                                            {myTripIcon.text}
                                        </ActionButton>
                                    </li>
                                )}
                            </Stacker>
                        </Styler>
                    </ModalContent>
                </Sidebar>
            )}
        </>
    );
}
